import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, MapPin, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import { PROJECTS, PROJECT_CATEGORIES, img } from "@/config";

export default function Projects() {
  const [filter, setFilter] = useState("All");
  const filtered = filter === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === filter);

  return (
    <div data-testid="projects-page">
      <PageHero
        eyebrow="Projects & Portfolio"
        titleLines={["900+ installations.", "One trusted partner."]}
        subtitle="From statewide police surveillance to enterprise and healthcare deployments — explore the projects that define our track record across India."
        imageKey="hero_cctv"
      />

      {/* Filters */}
      <section className="mx-auto max-w-7xl px-6 pt-8 lg:px-10">
        <div className="flex flex-wrap gap-2.5" data-testid="project-filters">
          {PROJECT_CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              data-testid={`filter-${cat.toLowerCase()}`}
              className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${
                filter === cat ? "border-[#2563EB] bg-[#2563EB] text-white" : "border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-7xl px-6 py-12 lg:px-10 lg:py-16">
        <motion.div layout className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {filtered.map((p) => (
              <motion.div
                layout
                key={p.slug}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.35 }}
              >
                <Link to={`/projects/${p.slug}`} data-testid={`project-card-${p.slug}`} className="group relative block overflow-hidden rounded-3xl border border-slate-800">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={img(p.image)} alt={p.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120] via-[#0B1120]/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-[#2563EB] px-3 py-1 text-xs font-semibold text-white">{p.category}</span>
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-[#60A5FA]"><Camera className="h-3.5 w-3.5" /> {p.metric}</span>
                    </div>
                    <h3 className="mt-3 font-display text-lg font-bold tracking-tight text-white">{p.title}</h3>
                    <span className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-white opacity-0 transition-opacity group-hover:opacity-100">View case study <ArrowUpRight className="h-4 w-4" /></span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      {/* Coverage map */}
      <section className="mx-auto max-w-7xl px-6 pb-16 lg:px-10" data-testid="coverage-map-section">
        <Reveal>
          <div className="overflow-hidden rounded-3xl border border-slate-800">
            <div className="flex items-center gap-2.5 border-b border-slate-800 bg-[#111c33]/60 px-6 py-4">
              <MapPin className="h-5 w-5 text-[#2563EB]" />
              <p className="font-display font-bold text-white">Service coverage across India</p>
            </div>
            <iframe
              title="KAS Info Tech service coverage across India"
              src="https://www.google.com/maps?q=India&z=4&output=embed"
              width="100%"
              height="360"
              style={{ border: 0, filter: "invert(90%) hue-rotate(180deg) grayscale(20%)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </section>
    </div>
  );
}
